// "From" selector for the admin compose surfaces. Lists active
// emailSenderIdentity rows and reports the chosen one to the parent.
//
// Personal identities are listed first and preselected — a 1:1 admin email
// should come from a person with a real reply-to, not from service@, which is
// what makes it read as machine mail regardless of the body.

import { useEffect, useRef, useState } from 'react';
import client from '../lib/feathers';

export default function SenderPicker({ value, onChange, kind = 'personal' }) {
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  // Latch on "have we fetched" rather than on identities.length — an empty
  // list would otherwise never satisfy a length guard and the effect would
  // refetch on every render.
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    client.service('admin/email-sender-identity').find({ query: { isActive: true } })
      .then((r) => {
        const rows = r.data || [];
        const ordered = [
          ...rows.filter((x) => x.kind === kind),
          ...rows.filter((x) => x.kind !== kind),
        ];
        setIdentities(ordered);
        const preferred = ordered.find((x) => x.kind === kind && x.isDefault) || ordered[0];
        if (preferred && !value) onChange(preferred.id);
      })
      .catch(() => setIdentities([]))
      .finally(() => setLoading(false));
  }, [kind, value, onChange]);

  const selected = identities.find((x) => x.id === value);

  if (loading) {
    return <div className="text-xs text-gray-500">Loading senders…</div>;
  }

  if (!identities.length) {
    return (
      <div className="text-xs text-gray-500">
        No sender identities configured — sending as the default.{' '}
        <a href="/admin/email-senders" className="text-blue-600 hover:underline">Set one up</a>.
      </div>
    );
  }

  return (
    <div>
      <label htmlFor="sender-picker" className="block text-sm font-medium text-gray-700 mb-1">From</label>
      <select
        id="sender-picker"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
      >
        {identities.map((i) => (
          <option key={i.id} value={i.id}>
            {i.fromName} &lt;{i.fromEmail}&gt;{i.kind === 'system' ? ' — system' : ''}
          </option>
        ))}
      </select>
      {selected?.replyTo && (
        <p className="text-xs text-gray-500 mt-1">Replies go to {selected.replyTo}</p>
      )}
      {selected?.signatureHtml && (
        <p className="text-xs text-gray-500 mt-1">Your signature will be appended.</p>
      )}
    </div>
  );
}
